import { useState } from 'react';
import { Link } from 'react-router-dom';
import RevealOnScroll from '../../../components/base/RevealOnScroll';

const FORM_URL = 'https://readdy.ai/api/form/d71v3qohb0rqicn68oqg';
const BG_IMAGE = 'https://public.readdy.ai/ai/img_res/5cd745e22fc9e608156c411f719e3279.jpg';

export default function FinalCTA() {
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', service: '', message: '' });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (formData.message.length > 500) return;
    setStatus('sending');
    try {
      const params = new URLSearchParams();
      Object.entries(formData).forEach(([k, v]) => params.append(k, v));
      const res = await fetch(FORM_URL, { method: 'POST', headers: { 'Content-Type': 'application/x-www-form-urlencoded' }, body: params.toString() });
      setStatus(res.ok ? 'success' : 'error');
    } catch {
      setStatus('error');
    }
  };

  return (
    <section
      className="relative py-24 overflow-hidden"
      style={{ backgroundImage: `url('${BG_IMAGE}')`, backgroundSize: 'cover', backgroundPosition: 'center 40%', backgroundAttachment: 'fixed' }}
    >
      {/* Multi-layer overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-black/92 via-black/85 to-black/50" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-black/20" />
      {/* Brand accent */}
      <div className="absolute inset-y-0 left-0 w-1 bg-gradient-to-b from-transparent via-brand-gold to-transparent" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left: headline + proof */}
          <RevealOnScroll direction="left">
            <span className="text-brand-gold text-xs font-bold uppercase tracking-widest inline-flex items-center gap-2 mb-5">
              <span className="w-8 h-px bg-brand-gold inline-block" />
              Ready to Get Started?
            </span>
            <h2 className="font-display text-4xl lg:text-5xl font-bold text-white mb-5 leading-tight text-shadow-lg">
              Your Chicago Roof Deserves<br />
              <span className="text-brand-gold italic">Expert Attention.</span><br />
              Let&apos;s Do This Right.
            </h2>
            <p className="text-white/75 leading-relaxed mb-10 max-w-lg">
              Schedule your free inspection today. We&apos;ll assess your roof, provide a complete written
              report, and give you a fair, detailed estimate — with zero pressure and zero obligation.
            </p>

            <div className="flex flex-col gap-3.5 mb-10">
              {[
                { icon: 'ri-check-double-line', text: 'Free on-site inspection — no cost, no obligation' },
                { icon: 'ri-check-double-line', text: 'Written estimate with full material & labor breakdown' },
                { icon: 'ri-check-double-line', text: 'Response within 1 business day' },
                { icon: 'ri-check-double-line', text: 'Licensed & insured — family-owned since 1989' },
              ].map((item) => (
                <div key={item.text} className="flex items-center gap-3">
                  <div className="w-6 h-6 flex items-center justify-center bg-brand-gold/20 border border-brand-gold/30 rounded-full flex-shrink-0">
                    <i className={`${item.icon} text-brand-gold text-xs`} />
                  </div>
                  <span className="text-white/80 text-sm">{item.text}</span>
                </div>
              ))}
            </div>

            {/* Contact info */}
            <div className="flex flex-wrap gap-4">
              <a
                href="tel:7086984072"
                className="inline-flex items-center gap-2.5 bg-white/10 border border-white/20 hover:bg-white/20 text-white text-sm font-bold px-6 py-3.5 rounded-xl transition-all cursor-pointer whitespace-nowrap backdrop-blur-sm"
              >
                <div className="w-7 h-7 flex items-center justify-center bg-brand-gold/20 rounded-lg">
                  <i className="ri-phone-fill text-brand-gold text-sm" />
                </div>
                (708) 698-4072
              </a>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 border-2 border-white/30 hover:border-white/60 text-white/70 hover:text-white text-sm font-medium px-6 py-3.5 rounded-xl transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-mail-send-line text-sm" />
                Send an Email
              </Link>
            </div>
          </RevealOnScroll>

          {/* Right: form */}
          <RevealOnScroll direction="right" delay={120}>
            <div className="bg-white rounded-2xl p-8 lg:p-10">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 flex items-center justify-center bg-brand-green/10 rounded-xl">
                  <i className="ri-calendar-check-line text-brand-green text-lg" />
                </div>
                <h3 className="font-display text-2xl font-bold text-brand-dark">Request Your Free Inspection</h3>
              </div>
              <p className="text-gray-500 text-sm mb-7 ml-1">We respond within 1 business day</p>

              {status === 'success' ? (
                <div className="text-center py-10">
                  <div className="w-16 h-16 flex items-center justify-center bg-brand-green/10 rounded-full mx-auto mb-4">
                    <i className="ri-checkbox-circle-fill text-brand-green text-4xl" />
                  </div>
                  <div className="font-bold text-brand-dark text-lg mb-2">Request Received!</div>
                  <p className="text-gray-500 text-sm">We&apos;ll contact you shortly to schedule your free roof inspection. Questions? Call (708) 698-4072.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} data-readdy-form id="final-cta-form">
                  <div className="flex flex-col gap-4">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs font-bold text-gray-600 block mb-1.5">Full Name *</label>
                        <input
                          name="name" required value={formData.name} onChange={handleChange}
                          className="w-full border border-brand-cream-3 rounded-lg px-3.5 py-2.5 text-sm focus:outline-none focus:border-brand-green transition-colors"
                          placeholder="Your name"
                        />
                      </div>
                      <div>
                        <label className="text-xs font-bold text-gray-600 block mb-1.5">Phone Number *</label>
                        <input
                          name="phone" required value={formData.phone} onChange={handleChange} type="tel"
                          className="w-full border border-brand-cream-3 rounded-lg px-3.5 py-2.5 text-sm focus:outline-none focus:border-brand-green transition-colors"
                          placeholder="(708) 555-0000"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="text-xs font-bold text-gray-600 block mb-1.5">Email Address *</label>
                      <input
                        name="email" required value={formData.email} onChange={handleChange} type="email"
                        className="w-full border border-brand-cream-3 rounded-lg px-3.5 py-2.5 text-sm focus:outline-none focus:border-brand-green transition-colors"
                        placeholder="your@email.com"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-bold text-gray-600 block mb-1.5">Service Needed</label>
                      <select
                        name="service" value={formData.service} onChange={handleChange}
                        className="w-full border border-brand-cream-3 rounded-lg px-3.5 py-2.5 text-sm focus:outline-none focus:border-brand-green bg-white transition-colors"
                      >
                        <option value="">Select a service...</option>
                        <option>Flat Roofing</option>
                        <option>Shingle Roofing</option>
                        <option>Commercial Roofing</option>
                        <option>Tuckpointing &amp; Masonry</option>
                        <option>Chimney Repair</option>
                        <option>Sheet Metal &amp; Gutters</option>
                        <option>Vinyl Siding</option>
                        <option>Sealcoating &amp; Asphalt</option>
                        <option>Emergency Roof Repair</option>
                        <option>Not Sure / General Inspection</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-xs font-bold text-gray-600 block mb-1.5">Project Details</label>
                      <textarea
                        name="message" value={formData.message} onChange={handleChange}
                        rows={3} maxLength={500}
                        className="w-full border border-brand-cream-3 rounded-lg px-3.5 py-2.5 text-sm focus:outline-none focus:border-brand-green resize-none transition-colors"
                        placeholder="Briefly describe your roofing issue or project..."
                      />
                      <div className="text-right text-xs text-gray-400 mt-1">{formData.message.length}/500</div>
                    </div>
                    <button
                      type="submit"
                      disabled={status === 'sending' || formData.message.length > 500}
                      className="w-full bg-brand-gold hover:bg-brand-gold-dark text-white text-sm font-bold py-4 rounded-lg transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 flex items-center justify-center gap-2"
                    >
                      <i className="ri-calendar-check-line" />
                      {status === 'sending' ? 'Sending...' : 'Request Free Inspection'}
                    </button>
                    {status === 'error' && (
                      <p className="text-red-500 text-xs text-center">Something went wrong. Please call us at (708) 698-4072.</p>
                    )}
                  </div>
                </form>
              )}
            </div>
          </RevealOnScroll>
        </div>
      </div>
    </section>
  );
}
